import React, { useState } from "react";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import PageTitle from "../../layouts/PageTitle";
import { assignVehicleApi } from "../../../services/vehicle";
import {
  appApi,
  useGetAllStaffQuery,
  useGetVehiclesQuery,
  useGetVehicleAssignmentsQuery,
} from "../../../store/appApi";

const EMPTY_FORM = { vehicleId: "", employeeId: "", kilometers: "" };

const AssignVehicle = () => {
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ── Remote data ───────────────────────────────────────────────────────────
  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery({
    limit: 100,
  });
  const staff = Array.isArray(staffData?.staff) ? staffData.staff : [];

  const { data: vehicleData, isLoading: vehicleLoading } = useGetVehiclesQuery({
    limit: 100,
  });
  const vehicles = Array.isArray(vehicleData?.data)
    ? vehicleData.data.filter((v) => v.status === true)
    : [];

  const {
    data: assignmentData,
    isLoading: assignLoading,
    isFetching: assignFetching,
  } = useGetVehicleAssignmentsQuery();
  const assignments = Array.isArray(assignmentData?.assignments)
    ? assignmentData.assignments
    : [];

  const listLoading = assignLoading || assignFetching;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.vehicleId) e.vehicleId = "Please select a vehicle.";
    if (!form.employeeId) e.employeeId = "Please select an employee.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await assignVehicleApi(form.vehicleId, {
        employeeId: form.employeeId,
        kilometers: form.kilometers ? Number(form.kilometers) : 0,
      });

      dispatch(appApi.util.invalidateTags(["VehicleAssignments", "Vehicles"]));
      setForm(EMPTY_FORM);
      setErrors({});

      Swal.fire({
        icon: "success",
        title: "Assigned!",
        text: "Vehicle has been assigned successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to assign vehicle. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  return (
    <>
      <PageTitle activeMenu="Assign Vehicle" motherMenu="Vehicles / Machines" />

      <div className="row g-3">
        {/* ── Left: Assignment Form ─────────────────────────────────────────── */}
        <div className="col-xl-4 col-lg-5">
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fa fa-car me-2 text-primary" />
                New Assignment
              </h5>
            </div>

            <div className="card-body d-flex flex-column">
              <form onSubmit={handleSubmit} noValidate>
                {/* Vehicle */}
                <div className="form-group mb-3">
                  <label className="form-label fw-semibold" htmlFor="vehicleId">
                    Active Vehicles <span className="text-danger">*</span>
                  </label>
                  <select
                    id="vehicleId"
                    name="vehicleId"
                    className={`form-control ${errors.vehicleId ? "is-invalid" : ""}`}
                    value={form.vehicleId}
                    onChange={handleChange}
                    disabled={loading || vehicleLoading}
                  >
                    <option value="">
                      {vehicleLoading ? "Loading…" : "— Select Vehicle —"}
                    </option>
                    {vehicles.map((v) => (
                      <option key={v._id} value={v._id}>
                        {v.vehicleNumber} &nbsp;·&nbsp; {v.vehicleMake}{" "}
                        {v.vehicleModel}
                        {v.assignedTo ? ` (${v.assignedTo})` : ""}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleId && (
                    <div className="invalid-feedback">{errors.vehicleId}</div>
                  )}
                  <div className="form-text text-muted small">
                    Vehicles showing a name are already assigned — assigning
                    again will close the previous record.
                  </div>
                </div>

                {/* Employee */}
                <div className="form-group mb-3">
                  <label
                    className="form-label fw-semibold"
                    htmlFor="employeeId"
                  >
                    Employee <span className="text-danger">*</span>
                  </label>
                  <select
                    id="employeeId"
                    name="employeeId"
                    className={`form-control ${errors.employeeId ? "is-invalid" : ""}`}
                    value={form.employeeId}
                    onChange={handleChange}
                    disabled={loading || staffLoading}
                  >
                    <option value="">
                      {staffLoading ? "Loading…" : "— Select Employee —"}
                    </option>
                    {staff.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name}{" "}
                        {s.role ? `(${s.role.replace(/-/g, " ")})` : ""}
                      </option>
                    ))}
                  </select>
                  {errors.employeeId && (
                    <div className="invalid-feedback">{errors.employeeId}</div>
                  )}
                </div>

                {/* Kilometers */}
                <div className="form-group mb-4">
                  <label
                    className="form-label fw-semibold"
                    htmlFor="kilometers"
                  >
                    Starting Kilometers{" "}
                    <span className="text-muted small fw-normal">
                      (optional)
                    </span>
                  </label>
                  <div className="input-group">
                    <input
                      id="kilometers"
                      name="kilometers"
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="e.g. 12500"
                      value={form.kilometers}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <span className="input-group-text text-muted">km</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Assigning…
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check me-2" />
                      Assign Vehicle
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── Right: Active Assignments List ────────────────────────────────── */}
        <div className="col-xl-8 col-lg-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2 flex-shrink-0">
              <h5 className="card-title mb-0">
                <i className="fa fa-list me-2 text-primary" />
                Currently Assigned Vehicles
              </h5>
              {!listLoading && (
                <span className="badge badge-primary ms-1">
                  {assignments.length}
                </span>
              )}
            </div>

            <div
              className="card-body"
              style={{ overflowY: "auto", maxHeight: "calc(100vh - 250px)" }}
            >
              <div className="table-responsive" style={{ minWidth: 0 }}>
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>Vehicle</th>
                      <th>Assigned To</th>
                      <th>Role</th>
                      <th>Since</th>
                      <th>Start Km</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading && (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!listLoading && assignments.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-muted">
                          No vehicles are currently assigned.
                        </td>
                      </tr>
                    )}
                    {!listLoading &&
                      assignments.map((a, idx) => (
                        <tr key={a._id}>
                          {/* <td className="text-muted small">{idx + 1}</td> */}
                          <td>
                            <span
                              className="fw-semibold"
                              style={{ textTransform: "uppercase" }}
                            >
                              {a.vehicleNumber}
                            </span>
                            <br />
                            <span className="text-muted small">
                              {a.vehicleMake} {a.vehicleModel}
                            </span>
                          </td>
                          <td className="fw-semibold">{a.employeeName}</td>
                          <td>
                            <span
                              className="badge badge-dark"
                              style={{ textTransform: "capitalize" }}
                            >
                              {a.employeeRole?.replace(/-/g, " ") || "—"}
                            </span>
                          </td>
                          <td className="text-muted small">
                            {fmtDate(a.startDate)}
                          </td>
                          <td className="text-muted small">
                            {a.startKm
                              ? `${Number(a.startKm).toLocaleString("en-IN")} km`
                              : "—"}
                          </td>
                          <td>
                            <Link
                              style={{
                                borderColor: "#324336",
                              }}
                              to={`/vehicle/${a.vehicleId}/history`}
                              className="btn btn-xs btn-outline row"
                              title="View assignment history"
                            >
                              <div
                                className="d-flex align-items-center gap-1"
                                style={{ paddingVertical: "10px" }}
                              >
                                <i className="fa fa-history me-1" />
                                <div>History</div>
                              </div>
                            </Link>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignVehicle;
